import React, {useState, useEffect} from 'react';
import {useNavigate, useLocation, useParams, useSearchParams} from 'react-router-dom';
import {useAuth} from '../auth/AuthContext';
import api from '../api/axios';
import '../styles/FriendsPage.css';
import { useClickable } from '../providers/ClickableProvider';
import {
    Search, Users, UserPlus, Bell,
    UserCheck, UserMinus, UserX, Check
} from 'lucide-react';

// Интерфейсы для данных
interface Friend {
    id: string;
    name: string;
    nickname: string;
    avatar: string;
    aboutMe?: string;
    isOnline?: boolean;
    lastSeen?: string;
    mutualFriends?: number;
    status?: string;
    friendshipStatus?: string;
    isFriend?: boolean;
}

interface FriendGroup {
    letter: string;
    friends: Friend[];
}

const FriendsPage: React.FC = () => {
    const {isAuthenticated, user: currentUser} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const {nickname: profileNickname} = useParams<{ nickname?: string }>();
    const [searchParams] = useSearchParams();
    const { makeClickable } = useClickable();

    // Получаем активную вкладку из URL параметра, или используем 'friends' по умолчанию
    const urlTab = searchParams.get('tab') as 'friends' | 'followers' | 'following';
    const [activeTab, setActiveTab] = useState<'friends' | 'followers' | 'following'>(
        urlTab || 'friends'
    );
    const [friends, setFriends] = useState<Friend[]>([]);
    const [followers, setFollowers] = useState<Friend[]>([]);
    const [following, setFollowing] = useState<Friend[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInputValue, setSearchInputValue] = useState('');
    const [isOwnProfile, setIsOwnProfile] = useState(false);
    const [isLoadingAction, setIsLoadingAction] = useState<string | null>(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(searchInputValue);
        }, 500);

        return () => {
            clearTimeout(timer);
        };
    }, [searchInputValue]);

    // Определяем, чей это профиль
    useEffect(() => {
        if (profileNickname) {
            setIsOwnProfile(currentUser?.nickname === profileNickname);
        } else {
            setIsOwnProfile(location.pathname.includes('/profile') ||
                !location.pathname.includes('/users/'));
        }
    }, [profileNickname, currentUser, location.pathname]);

    // Функция загрузки всех данных
    const loadAllConnections = async () => {
        try {
            setLoading(true);

            if (isOwnProfile && isAuthenticated) {
                // Загружаем все данные для своего профиля
                await Promise.all([
                    fetchOwnConnectionsByType('friends'),
                    fetchOwnConnectionsByType('followers'),
                    fetchOwnConnectionsByType('following')
                ]);
            } else if (profileNickname) {
                // Загружаем все данные для чужого профиля
                await Promise.all([
                    fetchOtherUserConnectionsByType(profileNickname, 'friends'),
                    fetchOtherUserConnectionsByType(profileNickname, 'followers'),
                    fetchOtherUserConnectionsByType(profileNickname, 'following')
                ]);
            } else {
                // Неавторизованный доступ
                setFriends([]);
                setFollowers([]);
                setFollowing([]);
            }
        } catch (error) {
            console.error('Error loading all connections:', error);
        } finally {
            setLoading(false);
            setInitialLoadComplete(true);
        }
    };

    // Загрузка всех данных при монтировании
    useEffect(() => {
        loadAllConnections();
    }, [isOwnProfile, profileNickname, isAuthenticated]);

    // Обновляем URL при изменении активной вкладки
    const handleTabChange = (tab: 'friends' | 'followers' | 'following') => {
        setActiveTab(tab);
        // Обновляем URL с параметром tab
        if (profileNickname) {
            navigate(`?tab=${tab}`, {replace: true});
        } else {
            navigate(`/friends?tab=${tab}`, {replace: true});
        }
    };

    // Функции для загрузки данных по типам
    const fetchOwnConnectionsByType = async (type: 'friends' | 'followers' | 'following') => {
        try {
            switch (type) {
                case 'friends':
                    const friendsResponse = await api.get('/user/friends');
                    const formattedFriends: Friend[] = (friendsResponse.data || []).map((friend: any) => ({
                        id: friend.user?.id?.toString() || friend.id?.toString(),
                        name: friend.user?.nickname || 'Неизвестно',
                        nickname: friend.user?.nickname || '',
                        avatar: friend.user?.avatarUrl || '/user-images/default/avatar.jpg',
                        aboutMe: friend.user?.aboutMe,
                        isOnline: friend.user?.isOnline,
                        lastSeen: friend.user?.lastSeen,
                        mutualFriends: friend.mutualFriends,
                        status: friend.status || friend.friendshipStatus,
                        isFriend: true
                    }));
                    setFriends(formattedFriends);
                    break;

                case 'followers':
                    const followersResponse = await api.get('/user/friends/followers');
                    const formattedFollowers: Friend[] = (followersResponse.data || []).map((follower: any) => ({
                        id: follower.id?.toString() || Math.random().toString(),
                        name: follower.nickname || 'Неизвестно',
                        nickname: follower.nickname || '',
                        avatar: follower.avatarUrl || '/user-images/default/avatar.jpg',
                        aboutMe: follower.aboutMe,
                        mutualFriends: follower.mutualFriends,
                        status: follower.status || follower.friendshipStatus,
                        isFriend: follower.isFriend || false
                    }));
                    setFollowers(formattedFollowers);
                    break;

                case 'following':
                    const followingResponse = await api.get('/user/friends/following');
                    const formattedFollowing: Friend[] = (followingResponse.data || []).map((followingItem: any) => ({
                        id: followingItem.id?.toString() || Math.random().toString(),
                        name: followingItem.nickname || 'Неизвестно',
                        nickname: followingItem.nickname || '',
                        avatar: followingItem.avatarUrl || '/user-images/default/avatar.jpg',
                        aboutMe: followingItem.aboutMe,
                        status: followingItem.status || followingItem.friendshipStatus,
                        mutualFriends: followingItem.mutualFriends,
                        isFriend: followingItem.isFriend || false
                    }));
                    setFollowing(formattedFollowing);
                    break;
            }
        } catch (error) {
            console.error(`Error fetching ${type}:`, error);
        }
    };

    const fetchOtherUserConnectionsByType = async (nickname: string, type: 'friends' | 'followers' | 'following') => {
        try {
            switch (type) {
                case 'friends':
                    const friendsResponse = await api.get(`/users/${nickname}/friends`);
                    const formattedFriends: Friend[] = Array.isArray(friendsResponse.data)
                        ? friendsResponse.data.map((friend: any) => ({
                            id: friend.id?.toString() || Math.random().toString(),
                            name: friend.nickname || friend.username || 'Неизвестно',
                            nickname: friend.nickname || '',
                            avatar: friend.avatarUrl || '/user-images/default/avatar.jpg',
                            aboutMe: friend.aboutMe,
                            mutualFriends: friend.mutualFriends,
                            isFriend: true
                        }))
                        : [];
                    setFriends(formattedFriends);
                    break;

                case 'followers':
                    const followersResponse = await api.get(`/users/${nickname}/followers`);
                    const formattedFollowers: Friend[] = Array.isArray(followersResponse.data)
                        ? followersResponse.data.map((follower: any) => ({
                            id: follower.id?.toString() || Math.random().toString(),
                            name: follower.nickname || 'Неизвестно',
                            nickname: follower.nickname || '',
                            avatar: follower.avatarUrl || '/user-images/default/avatar.jpg',
                            aboutMe: follower.aboutMe,
                            mutualFriends: follower.mutualFriends
                        }))
                        : [];
                    setFollowers(formattedFollowers);
                    break;

                case 'following':
                    const followingResponse = await api.get(`/users/${nickname}/following`);
                    const formattedFollowing: Friend[] = Array.isArray(followingResponse.data)
                        ? followingResponse.data.map((followingItem: any) => ({
                            id: followingItem.id?.toString() || Math.random().toString(),
                            name: followingItem.nickname || 'Неизвестно',
                            nickname: followingItem.nickname || '',
                            avatar: followingItem.avatarUrl || '/user-images/default/avatar.jpg',
                            aboutMe: followingItem.aboutMe,
                            status: followingItem.status
                        }))
                        : [];
                    setFollowing(formattedFollowing);
                    break;
            }
        } catch (error) {
            console.error(`Error fetching ${type} for other user:`, error);
        }
    };

    // Обновить данные после действий
    const updateDataAfterAction = async () => {
        // Если есть активная вкладка, обновляем только её
        if (initialLoadComplete) {
            if (isOwnProfile && isAuthenticated) {
                await fetchOwnConnectionsByType(activeTab);
            } else if (profileNickname) {
                await fetchOtherUserConnectionsByType(profileNickname, activeTab);
            }
        } else {
            // Иначе загружаем все заново
            await loadAllConnections();
        }
    };

    // Действия с друзьями
    const handleFriendAction = async (action: string, friendId: string, nickname: string) => {
        if (!isAuthenticated || !isOwnProfile) return;

        setIsLoadingAction(friendId);

        try {
            switch (action) {
                case 'accept':
                    await api.post(`/user/friends/${friendId}/accept`);
                    break;
                case 'remove':
                    await api.delete(`/user/friends/${friendId}/remove`);
                    break;
                case 'unfollow':
                    await api.delete(`/user/friends/${nickname}/unfollow`);
                    break;
                case 'add':
                    await api.post('/user/friends/request', {nickname});
                    break;
            }

            // Обновляем данные после действия
            await updateDataAfterAction();

        } catch (error) {
            console.error('Friend action error:', error);
            alert('Произошла ошибка');
        } finally {
            setIsLoadingAction(null);
        }
    };

    // Группировка по алфавиту
    const groupByAlphabet = (items: Friend[]): FriendGroup[] => {
        const groups: { [key: string]: Friend[] } = {};

        items.forEach(item => {
            const firstLetter = item.name?.charAt(0).toUpperCase() || '#';
            if (!groups[firstLetter]) {
                groups[firstLetter] = [];
            }
            groups[firstLetter].push(item);
        });

        return Object.keys(groups)
            .sort()
            .map(letter => ({
                letter,
                friends: groups[letter].sort((a, b) => a.name.localeCompare(b.name))
            }))
            .filter(group => group.friends.length > 0);
    };

    // Фильтрация по поисковому запросу
    const filterItems = (items: Friend[]) => {
        if (!searchQuery.trim()) return items;

        const query = searchQuery.toLowerCase();
        return items.filter(item =>
            item.nickname.toLowerCase().includes(query)
        );
    };

    // Получение активных данных
    const getActiveData = () => {
        switch (activeTab) {
            case 'friends':
                return {
                    data: filterItems(friends),
                    title: 'Друзья',
                    fullTitle: isOwnProfile ? 'Друзья' : `Друзья ${profileNickname}`,
                    icon: <Users size={20}/>,
                    count: friends.length
                };
            case 'followers':
                return {
                    data: filterItems(followers),
                    title: 'Подписчики',
                    fullTitle: isOwnProfile ? 'Подписчики' : `Подписчики ${profileNickname}`,
                    icon: <UserPlus size={20}/>,
                    count: followers.length
                };
            case 'following':
                return {
                    data: filterItems(following),
                    title: 'Подписки',
                    fullTitle: isOwnProfile ? 'Подписки' : `Подписки ${profileNickname}`,
                    icon: <Bell size={20}/>,
                    count: following.length
                };
        }
    };

    const activeData = getActiveData();
    const groupedData = groupByAlphabet(activeData.data);

    const handleConnectionClick = (connectionNickname: string) => {
        if (connectionNickname && connectionNickname !== currentUser?.nickname) {
            navigate(`/users/${connectionNickname}`);
        }
    };

    // Получение статуса для отображения
    const getStatusText = (status?: string) => {
        if (!status) return '';

        switch (status) {
            case 'PENDING':
                return 'Ожидает ответа';
            case 'ACCEPTED':
                return 'Друг';
            case 'REJECTED':
                return 'Запрос отклонен';
            default:
                return status;
        }
    };

    // Обработчик кнопки "Все" на странице пользователя
    const handleViewAllConnections = () => {
        if (profileNickname) {
            // Для чужого профиля
            navigate(`/users/${profileNickname}/${activeTab}?tab=${activeTab}`);
        } else {
            // Для своего профиля
            navigate(`/${activeTab}?tab=${activeTab}`);
        }
    };

    if (!isAuthenticated && !profileNickname) {
        return (
            <div className="friends-auth-required">
                <div className="auth-required-content">
                    <Users size={64} color="#666"/>
                    <h2>Войдите в систему</h2>
                    <p>Для просмотра списка друзей необходимо войти в систему</p>
                    <button
                        className="auth-required-btn"
                        onClick={() => navigate('/')}
                    >
                        На главную
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="friends-page-container">
            <div className="friends-page-content">
                <div className="friends-main-content">
                    {/* Основной список */}
                    <div className="friends-list-container">
                        <div className="friends-page-title">
                            <h2>{activeData.fullTitle}</h2>
                            <div className="friends-total-count">
                                Всего: {activeData.count}
                            </div>
                        </div>

                        {loading && !initialLoadComplete ? (
                            <div className="friends-loading">
                                <div className="loading-spinner"></div>
                                <p>Загрузка...</p>
                            </div>
                        ) : activeData.data.length === 0 ? (
                            <div className="friends-empty">
                                <div className="empty-icon">
                                    {activeTab === 'friends' && <Users size={64} color="#666"/>}
                                    {activeTab === 'followers' && <UserPlus size={64} color="#666"/>}
                                    {activeTab === 'following' && <Bell size={64} color="#666"/>}
                                </div>
                                <p>
                                    {searchQuery
                                        ? 'По вашему запросу ничего не найдено'
                                        : activeTab === 'friends'
                                            ? 'У вас пока нет друзей'
                                            : activeTab === 'followers'
                                                ? 'У вас пока нет подписчиков'
                                                : 'У вас пока нет подписок'}
                                </p>
                            </div>
                        ) : (
                            <div className="friends-alphabet-list">
                                {groupedData.map((group) => (
                                    <div key={group.letter} className="alphabet-group">
                                        <div className="group-header">
                                            <span className="group-letter">{group.letter}</span>
                                            <div className="group-divider"></div>
                                        </div>

                                        <div className="group-friends">
                                            {group.friends.map((friend) => (
                                                <div
                                                    key={friend.id}
                                                    className="friend-card"
                                                >
                                                    <div
                                                        className="friend-avatar"
                                                        onClick={() => handleConnectionClick(friend.nickname)}
                                                        style={{cursor: 'pointer'}}
                                                    >
                                                        <img
                                                            src={friend.avatar}
                                                            alt={friend.name}
                                                            onError={(e) => {
                                                                e.currentTarget.src = '/user-images/default/avatar.jpg';
                                                            }}
                                                        />
                                                    </div>

                                                    <div
                                                        className="friend-info"
                                                        {...makeClickable(`/users/${friend.nickname}`, (e) => {
                                                            if (!e.ctrlKey && !e.metaKey && e.button !== 1) {
                                                                handleConnectionClick(friend.nickname);
                                                            }
                                                        })}
                                                        style={{cursor: 'pointer'}}
                                                    >
                                                        <h4 className="friend-name">{friend.name}</h4>
                                                        {friend.aboutMe && (
                                                            <p className="friend-bio">{friend.aboutMe}</p>
                                                        )}

                                                        {friend.mutualFriends !== undefined && friend.mutualFriends > 0 && (
                                                            <div className="mutual-friends">
                                                                {friend.mutualFriends} общих друзей
                                                            </div>
                                                        )}

                                                        {activeTab !== 'friends' && friend.status && (
                                                            <div className="friend-status-info">
                                                                {getStatusText(friend.status)}
                                                            </div>
                                                        )}

                                                        {activeTab === 'friends' && friend.isOnline !== undefined && (
                                                            <div className="friend-status">
                                                                <span
                                                                    className={`status-dot ${friend.isOnline ? 'online' : 'offline'}`}></span>
                                                                <span className="status-text">
                                                                {friend.isOnline ? 'В сети' : friend.lastSeen ? `Был(а) ${friend.lastSeen}` : 'Не в сети'}
                                                            </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {isOwnProfile && isAuthenticated && (
                                                        <div className="friend-actions">
                                                            {activeTab === 'followers' && friend.status === 'PENDING' && (
                                                                <button
                                                                    className="friend-page-action-btn accept"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleFriendAction('accept', friend.id, friend.nickname);
                                                                    }}
                                                                    disabled={isLoadingAction === friend.id}
                                                                    title="Принять запрос в друзья"
                                                                >
                                                                    {isLoadingAction === friend.id ? (
                                                                        <div className="loading-spinner-tiny"></div>
                                                                    ) : (
                                                                        <Check size={16}/>
                                                                    )}
                                                                </button>
                                                            )}

                                                            {activeTab !== 'friends' && activeTab === 'following' && friend.isFriend && (
                                                                <button
                                                                    className="friend-page-action-btn remove"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleFriendAction('remove', friend.id, friend.nickname);
                                                                    }}
                                                                    disabled={isLoadingAction === friend.id}
                                                                    title="Удалить из друзей"
                                                                >
                                                                    {isLoadingAction === friend.id ? (
                                                                        <div className="loading-spinner-tiny"></div>
                                                                    ) : (
                                                                        <UserMinus size={16}/>
                                                                    )}
                                                                </button>
                                                            )}

                                                            {activeTab === 'followers' && friend.status === 'REJECTED' && (
                                                                <button
                                                                    className="friend-page-action-btn add"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleFriendAction('add', friend.id, friend.nickname);
                                                                    }}
                                                                    disabled={isLoadingAction === friend.id}
                                                                    title="Добавить в друзья"
                                                                >
                                                                    {isLoadingAction === friend.id ? (
                                                                        <div className="loading-spinner-tiny"></div>
                                                                    ) : (
                                                                        <UserPlus size={16}/>
                                                                    )}
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Сайдбар */}
                    <div className="friends-sidebar">
                        <div className="search-container">
                            <div className="search-input-wrapper">
                                <Search size={18} color="#666"/>
                                <input
                                    type="text"
                                    placeholder="Поиск друзей..."
                                    value={searchInputValue}
                                    onChange={(e) => setSearchInputValue(e.target.value)}
                                    className="search-input"
                                />
                                {searchInputValue && (
                                    <button
                                        className="clear-search-btn"
                                        onClick={() => setSearchInputValue('')} // Очищаем searchInputValue
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="friends-tabs-vertical">
                            <button
                                className={`friends-tab-vertical ${activeTab === 'friends' ? 'active' : ''}`}
                                onClick={() => handleTabChange('friends')}
                            >
                                <Users size={18}/>
                                <span>Друзья</span>
                                <span className="tab-count">{friends.length}</span>
                            </button>

                            <button
                                className={`friends-tab-vertical ${activeTab === 'followers' ? 'active' : ''}`}
                                onClick={() => handleTabChange('followers')}
                            >
                                <UserPlus size={18}/>
                                <span>Подписчики</span>
                                <span className="tab-count">{followers.length}</span>
                            </button>

                            <button
                                className={`friends-tab-vertical ${activeTab === 'following' ? 'active' : ''}`}
                                onClick={() => handleTabChange('following')}
                            >
                                <Bell size={18}/>
                                <span>Подписки</span>
                                <span className="tab-count">{following.length}</span>
                            </button>
                        </div>

                        {searchQuery && (
                            <div className="search-info">
                                Найдено: {activeData.data.length}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FriendsPage;